import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import AreaMapPicker from "@/components/AreaMapPicker";
import { useConfirm } from "@/components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Play, DollarSign, Radar, CheckCircle2, Clock, AlertCircle, Star, ChevronRight, Trash2, Layers,
} from "lucide-react";

const STATUS_STYLES = {
  SUCCEEDED: { icon: CheckCircle2, cls: "text-[#047857] bg-[#D1FAE5]" },
  RUNNING: { icon: Loader2, cls: "text-[#B45309] bg-[#FEF3C7]", spin: true },
  READY: { icon: Clock, cls: "text-[#B45309] bg-[#FEF3C7]", spin: true },
  "TIMED-OUT": { icon: AlertCircle, cls: "text-[#B91C1C] bg-[#FEE2E2]" },
  FAILED: { icon: AlertCircle, cls: "text-[#B91C1C] bg-[#FEE2E2]" },
  ABORTED: { icon: AlertCircle, cls: "text-[#B91C1C] bg-[#FEE2E2]" },
};

const STAR_OPTIONS = [
  { v: "", label: "Any rating" },
  { v: "3.5", label: "3.5★ and up" },
  { v: "4", label: "4★ and up" },
  { v: "4.5", label: "4.5★ and up" },
];

export default function ProspectingPanel({ onOpenReview, compact = false }) {
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState([]);
  const [geometry, setGeometry] = useState(null);
  const [name, setName] = useState("");
  const [maxPer, setMaxPer] = useState(120);
  const [minStars, setMinStars] = useState("4");
  const [skipClosed, setSkipClosed] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [starting, setStarting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sweeps, setSweeps] = useState([]);
  const confirm = useConfirm();
  const [allOpen, setAllOpen] = useState(false);
  const [allData, setAllData] = useState(null);

  const loadSweeps = useCallback(async () => {
    try {
      const { data } = await api.get("/sweeps");
      setSweeps(data);
    } catch {}
  }, []);

  const deleteScan = async (s) => {
    if (!(await confirm({ title: "Delete this scan", description: `Delete "${s.name}" and its unreviewed results? Garages you already added stay in your database.`, confirmText: "Delete scan", destructive: true }))) return;
    try { await api.delete(`/scans/${s.id}`); toast.success("Scan deleted"); loadSweeps(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openAll = async () => {
    setAllOpen(true); setAllData(null);
    try { const { data } = await api.get("/scans/all", { params: { status: "pending", sort_by: "score", sort_dir: "desc" } }); setAllData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/sweeps/presets").then((r) => {
      setPresets(r.data.categories || []);
      setSelected((r.data.categories || []).filter((c) => c.recommended).map((c) => c.id));
    }).catch(() => {});
    loadSweeps();
  }, [loadSweeps]);

  useEffect(() => {
    const running = sweeps.filter((s) => !["SUCCEEDED", "FAILED", "ABORTED", "TIMED-OUT"].includes(s.status) || (!s.classified && s.status === "SUCCEEDED"));
    if (running.length === 0) return;
    const iv = setInterval(async () => {
      for (const s of running) { try { await api.get(`/sweeps/${s.id}`); } catch {} }
      loadSweeps();
    }, 6000);
    return () => clearInterval(iv);
  }, [sweeps, loadSweeps]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const payload = () => ({
    name: name || `Sweep ${new Date().toLocaleDateString()}`,
    geojson: geometry,
    category_ids: selected,
    max_per: maxPer,
    min_stars: minStars ? Number(minStars) : null,
    skip_closed: skipClosed,
  });

  const doEstimate = async () => {
    if (!geometry) { toast.error("Pick the area on the map first"); return; }
    if (selected.length === 0) { toast.error("Pick at least one category"); return; }
    try {
      const { data } = await api.post("/sweeps/estimate", payload());
      setEstimate(data);
      setConfirmOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runSweep = async () => {
    setStarting(true);
    try {
      await api.post("/sweeps", payload());
      toast.success("Scanning started — you can leave this page and come back");
      setConfirmOpen(false);
      setEstimate(null);
      loadSweeps();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  const controls = (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><Radar className="w-5 h-5 text-[#1E3A8A]" /> New scan</h3>

        <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Name this scan</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sweep-name" placeholder="e.g. Al Quoz round 1"
          className="w-full h-12 px-3 mb-4 rounded-lg border border-[#D1D5DB] text-base focus:outline-none focus:border-[#1E3A8A]" />

        <label className="block text-xs font-bold uppercase text-[#6B7280] mb-2">What to look for (Google categories)</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {presets.map((c) => (
            <button key={c.id} onClick={() => toggle(c.id)} data-testid={`category-${c.id}`}
              className={`px-3 min-h-[42px] rounded-full text-xs font-bold border transition-colors ${selected.includes(c.id) ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#374151] border-[#D1D5DB]"}`}>
              {c.label}
            </button>
          ))}
        </div>

        <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Quality filter</label>
        <select value={minStars} onChange={(e) => setMinStars(e.target.value)} data-testid="min-stars"
          className="w-full h-12 px-3 mb-3 rounded-lg border border-[#D1D5DB] text-base bg-white focus:outline-none focus:border-[#1E3A8A]">
          {STAR_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>

        <label className="flex items-center gap-2 mb-4 text-sm font-medium text-[#374151]">
          <input type="checkbox" checked={skipClosed} onChange={(e) => setSkipClosed(e.target.checked)} data-testid="skip-closed" className="w-5 h-5 accent-[#1E3A8A]" />
          Skip permanently closed places
        </label>

        <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Max places to pull</label>
        <input type="number" value={maxPer} onChange={(e) => setMaxPer(Number(e.target.value))} data-testid="sweep-maxper"
          className="w-full h-12 px-3 mb-4 rounded-lg border border-[#D1D5DB] text-base focus:outline-none focus:border-[#1E3A8A]" />

        <button onClick={doEstimate} data-testid="estimate-btn"
          className="w-full h-12 rounded-lg bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#375DBA] active:scale-[0.98] transition-colors">
          <Play className="w-4 h-4" /> Check cost & start
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase text-[#6B7280]">Recent scans</h3>
          <button onClick={openAll} data-testid="see-all-scans" className="text-xs font-bold text-[#1E3A8A] flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> See all found</button>
        </div>
        <div className="space-y-2">
          {sweeps.length === 0 && <p className="text-sm text-[#9CA3AF]">No scans yet.</p>}
          {sweeps.map((s) => {
            const st = STATUS_STYLES[s.status] || STATUS_STYLES.RUNNING;
            const Icon = st.icon;
            const ready = s.status === "SUCCEEDED" && s.finalized;
            return (
              <div key={s.id} data-testid={`sweep-item-${s.id}`} className="flex items-stretch gap-2">
                <button onClick={() => ready && onOpenReview(s)}
                  className={`flex-1 min-w-0 text-left border border-[#E5E7EB] rounded-lg p-3 min-h-[56px] ${ready ? "hover:border-[#1E3A8A] cursor-pointer" : "cursor-default"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-[#111827] text-sm truncate">{s.name}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold shrink-0 ${st.cls}`}>
                      <Icon className={`w-3 h-3 ${st.spin ? "animate-spin" : ""}`} /> {s.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1">
                    {ready ? (
                      s.classified
                        ? <>{s.pending_count} to review of {s.result_count} found <ChevronRight className="w-3 h-3" /></>
                        : <>{s.result_count} found · AI is scoring them…</>
                    ) : "Scanning Google Maps…"}
                  </div>
                </button>
                <button onClick={() => deleteScan(s)} data-testid={`delete-scan-${s.id}`} aria-label="Delete scan"
                  className="w-11 shrink-0 rounded-lg border border-[#E5E7EB] text-[#9CA3AF] hover:text-[#B91C1C] hover:border-[#FCA5A5] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className={compact ? "space-y-4" : "grid grid-cols-1 lg:grid-cols-12 gap-6"}>
      <div className={compact ? "" : "lg:col-span-7 space-y-3"}>
        <AreaMapPicker onGeometry={setGeometry} pastAreas={sweeps.filter((s) => s.geojson).map((s) => ({ geojson: s.geojson, name: s.name }))} heightClass={compact ? "h-[46vh] min-h-[300px]" : "h-[52vh] min-h-[380px]"} />
      </div>
      <div className={compact ? "" : "lg:col-span-5"}>{controls}</div>

      <Dialog open={allOpen} onOpenChange={setAllOpen}>
        <DialogContent className="max-w-lg" data-testid="all-scans-dialog">
          <DialogHeader><DialogTitle className="text-lg font-black text-[#1E3A8A]">All prospects found</DialogTitle></DialogHeader>
          {!allData ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A]" /></div>
          ) : (
            <>
              <p className="text-sm text-[#6B7280] -mt-2">{allData.shown} unique prospects still to review across every scan (duplicates merged).</p>
              <div className="space-y-1.5 max-h-[55vh] overflow-y-auto mt-1">
                {allData.rows.length === 0 && <p className="text-sm text-[#9CA3AF] py-6 text-center">Nothing left to review.</p>}
                {allData.rows.slice(0, 150).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E7EB]" data-testid={`all-scan-row-${r.id}`}>
                    <span className="w-8 h-8 rounded-md bg-[#111827] text-white text-xs font-black flex items-center justify-center shrink-0">{(r.classification || {}).score ?? "—"}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold text-sm text-[#111827] truncate">{r.name}</span>
                      <span className="block text-xs text-[#6B7280] truncate">{r.area || "—"}{r.appeared_in > 1 ? ` · in ${r.appeared_in} scans` : ""}</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {confirmOpen && estimate && (
        <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="confirm-sweep">
            <h3 className="text-xl font-black text-[#1E3A8A] mb-2">Start this scan?</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Searching {estimate.category_count} Google categories inside your area. AI will score every result.
            </p>
            <div className="space-y-2 mb-5">
              <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Places at most</span><span className="font-bold">{estimate.estimated_places_upper_bound}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Cost at most</span><span className="font-bold flex items-center"><DollarSign className="w-4 h-4" />{estimate.estimated_cost_usd}</span></div>
              {minStars && <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Quality filter</span><span className="font-bold flex items-center gap-1"><Star className="w-3.5 h-3.5 text-[#F59E0B]" />{minStars}+</span></div>}
            </div>
            {estimate.overlap_count > 0 && (
              <div className="flex items-start gap-2 bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-3 mb-4 text-sm text-[#92400E]" data-testid="overlap-warning">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span><b>{estimate.overlap_count}</b> place{estimate.overlap_count !== 1 ? "s" : ""} in this area {estimate.overlap_count !== 1 ? "are" : "is"} already in your database — you'd likely pay to scrape {estimate.overlap_count !== 1 ? "them" : "it"} again. The grey areas on the map show where you've already scanned.</span>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setConfirmOpen(false)} className="flex-1 h-12 rounded-lg border border-[#D1D5DB] font-bold">Cancel</button>
              <button onClick={runSweep} disabled={starting} data-testid="confirm-run-btn"
                className="flex-1 h-12 rounded-lg bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Start
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
